import { useBatches } from '@ctcells/api'
import { dataSourceFor, LIST_PAGE_SIZE, PATHS } from '@ctcells/core'
import { errorMessage } from '@ctcells/i18n'
import { useNavigate } from 'react-router'

import { noticeToneOf } from '../atoms/noticeTone.ts'

import { BatchesScreen } from '../screens/batches/BatchesScreen.tsx'
import { useBatchOwners } from '../screens/batches/useBatchOwners.tsx'
import { DEMO_BATCH_HISTORY, DEMO_BATCH_LIST } from '../screens/demoData.ts'
import { HistoryScreen } from '../screens/history/HistoryScreen.tsx'
import {
  listPageCount,
  listPageQuery,
  listPageSlice,
  useListPage,
  type ListPaging,
} from '../screens/listPaging.ts'
import type { OpsTabId } from '../screens/ops/opsTabs.ts'
import { useScreenAccess } from '../screens/screenAccess.ts'
import { toBatchListRow } from './batchModel.ts'

/**
 * 작업(배치) · 이력 **컨테이너**. 쵸단 담당.
 *
 * 🔴 화면(`BatchesScreen` · `HistoryScreen`)은 한 줄도 바뀌지 않았다. 훅을 부르고 props 로 내려 준다.
 * 🔴 **데이터 소스가 갈리는 층은 여기 하나다**(`dataSourceFor('batches')`). fetch 어댑터나 목
 *    토글로 내리지 않는다 — 그러면 「실물을 때렸는데 목이 답했다」가 되어 실물 검증이 사라진다.
 * 🔴 `access.permissions` 는 **세션이 준 역할**에서 온다. `controlDisabled` 는 구독이 준다 —
 *    스트림이 끊기면 상태변경 버튼이 전부 비활성된다(화면이 아니라 `gate()` 가 막는다).
 */

// ⚠ `lockHeld` 를 넘기지 않는다 — 리스 조회 경로가 계약에 없어 **보유 여부를 모른다**.
//   획득 응답으로만 알 수 있고, 모르면 비활성 + 사유가 안전한 기본값이다.

/**
 * 🔴 **「못 봤다」를 「0건」으로 접지 않는다**(2026-09-22 실물 버그). 종전에는 `batches.isError`
 *    를 아무도 안 봐서, 조회가 실패하면 `rows: []` · `loading: false` 가 나가고 화면이
 *    **「작업 없음」**을 그렸다. 실제로 그랬다 — 목록 요청을 막은 채 `/batches` 를 열면
 *    빈 표가 멀쩡히 섰다. 「서버를 못 봤다」와 「도는 작업이 없다」는 **정반대 사실**이다.
 * 🔴 **자료는 `GET /batches` 한 번이 전부다**(2026-09-26). 레시피 이름·시료 수가 응답
 *    칸으로 온다(`recipe_name` · `sample_count`) — 종전의 `/recipes` 별도 조회를
 *    걷었다. **되살리지 마라** — 안 그리는 값을 위해 네트워크를 쓰지 않는다.
 * ⚠ 예외 하나: **admin 이면** 작업자 이름을 풀려고 `GET /users` 를 읽는다(`screens/batches/useBatchOwners.tsx` · 2026-09-30).
 *   operator · viewer 는 그 요청이 0건이다.
 */
function useBatchRows(phase: 'active' | 'finished') {
  const live = dataSourceFor('batches') === 'live'
  /*
   * 🔴 **한 쪽씩 서버에서 받는다**(eric 2026-09-30 「리스트로 만들어줘」 · `screens/listPaging.ts`).
   *    종전에는 200건을 한 번에 받아 전부 흘렸다(「정렬 파라미터가 없어 페이지 이동이 무의미하다」 —
   *    틀린 말이었다. 서버가 `created_at desc` · 이력은 `completed_at desc` 로 **줄을 세워 준다**).
   * 🔴 쪽마다 쿼리 키가 달라 구독 무효화(`keys.batches.all()` 접두)가 **지금 보는 쪽**을 다시 받는다.
   *    폴링이 아니다(DP-01).
   * 🔴 쪽 상태는 이 컨테이너가 쥔다 — 서랍을 열고 닫아도 두 주소가 같은 요소를 그려 쪽이 남는다.
   */
  const paging = useListPage()
  // 🔴 demo 면 요청 자체를 보내지 않는다 — 「실물을 때렸는데 목이 답했다」가 생기지 않게
  const batches = useBatches({
    ...listPageQuery(paging.page, LIST_PAGE_SIZE),
    phase,
    skip: !live,
  })
  /*
   * ⚠ 「진행」 열은 2026-09-30 에 걷었다(eric 「진행 중 탭 컬럼도.. 이력 컬럼에서 소요 시간 빼고 동일하게」) — 그 열의
   *   출처였던 구독 보관소(`getStreamProgress` · `selectBatchProgress`) 연결도 같이 걷었다. 진행은 상시 작업 줄이 말한다.
   */

  if (!live) {
    // 데모는 서버가 없어 받은 목록을 자른다 — 모양만 live 와 같게
    const all = phase === 'active' ? DEMO_BATCH_LIST : DEMO_BATCH_HISTORY
    const pageCount = paging.settle(
      listPageCount(all.length, paging.page, 0, LIST_PAGE_SIZE),
      false,
    )
    return {
      rows: listPageSlice(all, paging.page, LIST_PAGE_SIZE),
      loading: false,
      notice: undefined,
      noticeTone: undefined,
      paging: {
        page: Math.min(paging.page, pageCount),
        pageCount,
        totalCount: all.length,
        onChange: paging.setPage,
      } satisfies ListPaging,
    }
  }

  const items = batches.data?.items ?? []
  const totalCount = batches.data?.total_count ?? null
  const pageCount = paging.settle(
    listPageCount(totalCount, paging.page, items.length, LIST_PAGE_SIZE),
    // 🔴 실패도 «모름»이다 — 쪽 수를 1로 접지 않는다(다시 받으면 되살아난다)
    batches.isPending || batches.isError,
  )

  return {
    paging: {
      page: paging.page,
      pageCount,
      totalCount,
      onChange: paging.setPage,
    } satisfies ListPaging,
    rows: items.map((batch) => toBatchListRow(batch)),
    loading: batches.isPending,
    notice: batches.isError ? errorMessage(batches.error) : undefined,
    /*
     * 🔴 작업 목록은 `monitor` 만 있으면 200 이라 여기 사유는 대개 **「깨진 것」이지
     *    「막힌 것」이 아니다.** 그래서 톤을 에러에서 뽑는다 — 403 이면 차분하게, 아니면 위험색.
     */
    noticeTone: batches.isError ? noticeToneOf(batches.error) : undefined,
  }
}

/**
 * 두 어댑터가 같이 받는 것.
 *
 * @param opsTab 열린 무리 탭. 🔴 **라우트 표가 넘긴다** — 탭이 곧 주소다.
 *   **안 넘기면 「작업」 무리 탭 줄을 안 그린다**(`useGroupTabLayout` 주석). 안 넘기는 표면이
 *   둘이다 — **태블릿 kiosk**(넘기면 콘솔 경로로 튕겨 나간다)와 **장비 관점 콘솔**
 *   (거기선 작업·이력·알람이 사이드바에 각자 서 있어서 탭 줄이 또 뜨면 안 된다).
 */
export interface OpsRouteProps {
  opsTab?: OpsTabId | undefined
  /** 오른쪽 판에 열린 작업(작업 목록만) — 그 행이 고른 표시를 받는다 */
  selectedBatchId?: string | undefined
}

/**
 * 작업 목록 — 진행 중(`phase=active`).
 * ⚠ 실물에 `running`·`paused` 배치가 **0건**이라 화면이 비어 보일 수 있다. 그게 정답이다.
 */
export function BatchesRoute({ opsTab, selectedBatchId }: OpsRouteProps = {}) {
  const navigate = useNavigate()
  const access = useScreenAccess()
  const { rows, loading, notice, noticeTone, paging } = useBatchRows('active')
  // 🔴 admin 에게만 값이 있다 — 없으면 「작업자」 열 자체가 안 선다
  const owners = useBatchOwners()

  return (
    <BatchesScreen
      {...(owners === undefined ? {} : { owners })}
      {...(opsTab === undefined ? {} : { opsTab })}
      {...(notice === undefined ? {} : { notice })}
      {...(noticeTone === undefined ? {} : { noticeTone })}
      access={access}
      loading={loading}
      paging={paging}
      rows={rows}
      onNewBatch={() => void navigate(PATHS.batchNew)}
      onOpenBatch={(batchId) => void navigate(`${PATHS.batches}/${batchId}`)}
      {...(selectedBatchId === undefined ? {} : { selectedBatchId })}
    />
  )
}

/** 이력 — 종착(`phase=finished`). 실물 35건이 전부 여기 있다(completed 14 · aborted 21) */
export type HistoryRouteProps = OpsRouteProps

export function HistoryRoute({ opsTab, selectedBatchId }: HistoryRouteProps = {}) {
  const navigate = useNavigate()
  const access = useScreenAccess()
  const { rows, loading, notice, noticeTone, paging } = useBatchRows('finished')
  // 🔴 admin 에게만 값이 있다 — 없으면 「작업자」 열 자체가 안 선다
  const owners = useBatchOwners()

  return (
    <HistoryScreen
      {...(owners === undefined ? {} : { owners })}
      {...(opsTab === undefined ? {} : { opsTab })}
      {...(selectedBatchId === undefined ? {} : { selectedBatchId })}
      {...(notice === undefined ? {} : { notice })}
      {...(noticeTone === undefined ? {} : { noticeTone })}
      access={access}
      loading={loading}
      paging={paging}
      rows={rows}
      onOpenBatch={(batchId) => void navigate(`${PATHS.batchHistory}/${batchId}`)}
    />
  )
}
